import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Drawer,
    FormControlLabel,
    Grid,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoadingCard from '../common/LoadingCard';

const roleLabels = {
    6: 'Dispatcher',
    7: 'Supervisor Dispatcher',
};

const defaultDialogState = { open: false, title: '', message: '', onConfirm: null };

const DispatcherAccessDrawer = ({ getDispatcherApi, updateDispatcherApi }) => {
    const [open, setOpen] = useState(false);
    const [item, setItem] = useState(null);
    const [availableDispatchers, setAvailableDispatchers] = useState([]);
    const [availableContexts, setAvailableContexts] = useState([]);
    const [groupDispatchers, setGroupDispatchers] = useState([]);
    const [groupContexts, setGroupContexts] = useState([]);
    const [assignedDispatchers, setAssignedDispatchers] = useState([]);
    const [initialAssignments, setInitialAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState(defaultDialogState);

    const dispatcherMap = useMemo(() => {
        const map = new Map();
        availableDispatchers.forEach((dispatcher) => {
            map.set(dispatcher.id, dispatcher);
        });
        return map;
    }, [availableDispatchers]);

    const contextMap = useMemo(() => {
        const map = new Map();
        availableContexts.forEach((context) => {
            map.set(context.id, context);
        });
        return map;
    }, [availableContexts]);

    const combinedDispatchers = useMemo(() => {
        const seen = new Set();
        const merged = [];

        groupDispatchers.forEach((dispatcher) => {
            if (dispatcher?.id && !seen.has(dispatcher.id)) {
                merged.push(dispatcher);
                seen.add(dispatcher.id);
            }
        });

        availableDispatchers.forEach((dispatcher) => {
            if (dispatcher?.id && !seen.has(dispatcher.id)) {
                merged.push(dispatcher);
                seen.add(dispatcher.id);
            }
        });

        return merged;
    }, [groupDispatchers, availableDispatchers]);

    const normalizeAssignments = (list) => list
        .filter((entry) => !entry.inherited)
        .map((entry) => ({
            dispatcher_id: entry.dispatcher_id,
            context_user_id: entry.context_user_id,
            can_edit: false,
        }))
        .sort((a, b) => a.dispatcher_id - b.dispatcher_id);

    const hasChanges = () => {
        const current = JSON.stringify(normalizeAssignments(assignedDispatchers));
        const initial = JSON.stringify(normalizeAssignments(initialAssignments));
        return current !== initial;
    };

    const buildChangeSummary = () => {
    const summary = [];
    const editableInitial = initialAssignments.filter((entry) => !entry.inherited);
    const editableAssigned = assignedDispatchers.filter((entry) => !entry.inherited);

    const initialMap = new Map(editableInitial.map((entry) => [entry.dispatcher_id, entry]));
    const currentMap = new Map(editableAssigned.map((entry) => [entry.dispatcher_id, entry]));

        currentMap.forEach((current, dispatcherId) => {
            const dispatcher = dispatcherMap.get(dispatcherId);
            const name = dispatcher?.name ?? `#${dispatcherId}`;
            const contextName = contextMap.get(current.context_user_id)?.name ?? `Context #${current.context_user_id}`;

            if (!initialMap.has(dispatcherId)) {
                summary.push(`grant ${name} (${contextName})`);
            } else {
                const initial = initialMap.get(dispatcherId);
                if (initial.context_user_id !== current.context_user_id) {
                    summary.push(`set ${name} context to ${contextName}`);
                }
            }
        });

        initialMap.forEach((initial, dispatcherId) => {
            if (!currentMap.has(dispatcherId)) {
                const dispatcher = dispatcherMap.get(dispatcherId);
                const name = dispatcher?.name ?? `#${dispatcherId}`;
                summary.push(`remove ${name}`);
            }
        });

        return summary;
    };

    const handleToggleDispatcher = (dispatcher) => {
        if (!availableContexts.length) {
            setError('No contexts are available to assign dispatchers.');
            return;
        }

        setAssignedDispatchers((prev) => {
            const exists = prev.find((entry) => entry.dispatcher_id === dispatcher.id);
            if (exists) {
                if (exists.inherited) {
                    setError('Dispatcher access is inherited from group settings. Update the group to remove it.');
                    return prev;
                }
                return prev.filter((entry) => entry.dispatcher_id !== dispatcher.id);
            }

            const ownerContextId = contextMap.has(dispatcher.owner_id) ? dispatcher.owner_id : null;
            const fallbackContextId = ownerContextId ?? availableContexts[0]?.id ?? null;

            if (!fallbackContextId) {
                setError('Unable to determine a context for this dispatcher.');
                return prev;
            }

            return [
                ...prev,
                {
                    dispatcher_id: dispatcher.id,
                    context_user_id: Number(fallbackContextId),
                    can_view: true,
                    can_edit: false,
                    inherited: false,
                },
            ];
        });
    };

    const onClose = () => {
        setOpen(false);
    setItem(null);
    setError(null);
    setSuccess(null);
    setAssignedDispatchers([]);
    setInitialAssignments([]);
    setAvailableDispatchers([]);
    setAvailableContexts([]);
    setGroupDispatchers([]);
    setGroupContexts([]);
    setConfirmDialog(defaultDialogState);
    };

    const parseResponse = async (response) => {
        if (response.ok) {
            return response.json();
        }

        let message = 'Network response was not ok';
        try {
            const data = await response.json();
            if (data?.error) {
                message = data.error;
            }
        } catch (e) {
            // ignore parsing issues
        }

        throw new Error(message);
    };

    const fetchItemData = (itemId) => {
        setLoading(true);
        setError(null);

        const itemUrl = getDispatcherApi.replace('item:', itemId);

        fetch(itemUrl)
            .then(parseResponse)
            .then((data) => {
                setItem(data.item);
                const dispatchers = data.available_dispatchers ?? [];
                const contexts = data.available_contexts ?? [];
                const inheritedDispatchers = data.group_dispatcher_options ?? [];
                const inheritedContexts = data.group_context_options ?? [];
                const ownerContextId = contexts.find((context) => context.type === 'owner')?.id ?? contexts[0]?.id ?? null;

                setAvailableDispatchers(dispatchers);
                setAvailableContexts(contexts);
                setGroupDispatchers(inheritedDispatchers);
                setGroupContexts(inheritedContexts);

                const normalizedAssignments = (data.assigned_dispatchers ?? []).map((entry) => {
                    const contextId = entry.context_user_id ?? ownerContextId;
                    return {
                        dispatcher_id: entry.dispatcher_id,
                        context_user_id: contextId !== null ? Number(contextId) : null,
                        can_view: entry.can_view !== undefined ? !!entry.can_view : true,
                        can_edit: false,
                        inherited: entry.inherited !== undefined ? !!entry.inherited : false,
                        source: entry.source ?? 'item',
                    };
                }).filter((entry) => entry.context_user_id !== null);

                setAssignedDispatchers(normalizedAssignments);
                setInitialAssignments(normalizedAssignments);
                setLoading(false);
            })
            .catch((fetchError) => {
                console.error('Error fetching dispatcher data:', fetchError);
                setLoading(false);
                setError(fetchError.message || 'Failed to load dispatcher data');
            });
    };

    const handleSave = () => {
        const changes = buildChangeSummary();

        if (changes.length === 0) {
            setError('No changes to save');
            return;
        }

        const message = `Are you sure you want to ${changes.join(' and ')}?`;

        setConfirmDialog({
            open: true,
            title: 'Confirm Dispatcher Changes',
            message,
            onConfirm: performSave,
        });
    };

    const performSave = () => {
        setSaving(true);
        setError(null);
        setSuccess(null);

        const updateUrl = updateDispatcherApi.replace('item:', item.id);
        const payload = {
            assigned_dispatchers: assignedDispatchers
                .filter((entry) => !entry.inherited)
                .map((entry) => ({
                dispatcher_id: entry.dispatcher_id,
                context_user_id: entry.context_user_id,
                can_view: entry.can_view !== undefined ? entry.can_view : true,
        can_edit: false,
                })),
        };

        fetch(updateUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            },
            body: JSON.stringify(payload),
        })
            .then(parseResponse)
            .then((data) => {
                setSuccess(data.message ?? 'Dispatcher access updated successfully');
                setItem(data.item);
                const dispatchers = data.available_dispatchers ?? [];
                const contexts = data.available_contexts ?? [];
                const inheritedDispatchers = data.group_dispatcher_options ?? [];
                const inheritedContexts = data.group_context_options ?? [];
                const ownerContextId = contexts.find((context) => context.type === 'owner')?.id ?? contexts[0]?.id ?? null;

                setAvailableDispatchers(dispatchers);
                setAvailableContexts(contexts);
                setGroupDispatchers(inheritedDispatchers);
                setGroupContexts(inheritedContexts);

                const normalizedAssignments = (data.assigned_dispatchers ?? []).map((entry) => {
                    const contextId = entry.context_user_id ?? ownerContextId;
                    return {
                        dispatcher_id: entry.dispatcher_id,
                        context_user_id: contextId !== null ? Number(contextId) : null,
                        can_view: entry.can_view !== undefined ? !!entry.can_view : true,
                        can_edit: false,
                        inherited: entry.inherited !== undefined ? !!entry.inherited : false,
                        source: entry.source ?? 'item',
                    };
                }).filter((entry) => entry.context_user_id !== null);

                setAssignedDispatchers(normalizedAssignments);
                setInitialAssignments(normalizedAssignments);
                setSaving(false);

                if (window.table && window.table.ajax) {
                    window.table.ajax.reload();
                }
            })
            .catch((updateError) => {
                console.error('Error updating dispatcher access:', updateError);
                setError(updateError.message || 'Failed to update dispatcher access');
                setSaving(false);
            });

        setConfirmDialog(defaultDialogState);
    };

    useEffect(() => {
        const handleDispatcherSettings = (event) => {
            const { itemId } = event.detail;
            setOpen(true);
            fetchItemData(itemId);
        };

        window.addEventListener('items:dispatcherSettings', handleDispatcherSettings);

        return () => {
            window.removeEventListener('items:dispatcherSettings', handleDispatcherSettings);
        };
    }, []);

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: { width: 480 },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Dispatcher Access</Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {loading && <LoadingCard />}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {item && !loading && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    Item: {item.name}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'grid', gap: 0.5 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        Dispatchers act on behalf of their owner account.
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Update the dispatcher owner from the Users page to change who they represent.
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Entries labeled "Inherited from group" are managed inside the group access drawer.
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Dispatchers and supervisor dispatchers always remain view-only.
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                {combinedDispatchers.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary">
                                        No dispatchers are available for this owner or sub-account.
                                    </Typography>
                                ) : (
                                    combinedDispatchers.map((dispatcher) => {
                                        const isSelected = assignedDispatchers.some((entry) => entry.dispatcher_id === dispatcher.id);
                                        const assignedEntry = assignedDispatchers.find((entry) => entry.dispatcher_id === dispatcher.id);

                                        const roleLabel = roleLabels[dispatcher.role_id] ?? 'Dispatcher';
                                        const ownerLabel = dispatcher.owner_name ? `Owner: ${dispatcher.owner_name}` : null;
                                        const isInherited = assignedEntry?.inherited || groupDispatchers.some((inherited) => inherited.id === dispatcher.id);
                                        const inheritedLabel = 'Inherited from group';

                                        return (
                                            <Box
                                                key={dispatcher.id}
                                                sx={{
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                    py: 1.5,
                                                    '&:last-of-type': { borderBottom: 'none' },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <FormControlLabel
                                                        control={(
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={() => handleToggleDispatcher(dispatcher)}
                                                                disabled={saving || isInherited}
                                                            />
                                                        )}
                                                        label={dispatcher.name}
                                                    />
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {roleLabel}{ownerLabel ? ` · ${ownerLabel}` : ''}
                                                        </Typography>
                                                        {isInherited && (
                                                            <Chip size="small" label={inheritedLabel} variant="outlined" />
                                                        )}
                                                    </Box>
                                                </Box>

                                            </Box>
                                        );
                                    })
                                )}
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={!hasChanges() || saving}
                                    startIcon={saving ? <CircularProgress size={20} /> : null}
                                    fullWidth
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Drawer>

            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog(defaultDialogState)}
            >
                <DialogTitle>{confirmDialog.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDialog(defaultDialogState)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDialog.onConfirm}
                        color="primary"
                        variant="contained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DispatcherAccessDrawer;
